import * as React from 'react';
import './SearchMultiSelectFilter.scss';

export interface SearchMultiSelectFilterOption {
  value: string;
  label: string;
}

interface Props {
  label: string;
  options: SearchMultiSelectFilterOption[];
  selected: string[];
  onToggle: (value: string) => void;
  placeholder?: string;
  emptyText?: string;
  disabledValues?: string[];
  className?: string;
}

export const SearchMultiSelectFilter: React.FC<Props> = ({
  label,
  options,
  selected,
  onToggle,
  placeholder,
  emptyText = 'No matching registration forms',
  disabledValues = [],
  className = ''
}) => {
  const [search, setSearch] = React.useState('');
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const selectedValues = React.useMemo(() => new Set(selected.map(String)), [selected]);
  const disabled = React.useMemo(() => new Set(disabledValues.map(String)), [disabledValues]);
  const selectedOptions = options.filter(option => selectedValues.has(String(option.value)));
  const matchingOptions = normalizedSearch
    ? options.filter(
        option =>
          !selectedValues.has(String(option.value)) && option.label.toLocaleLowerCase().includes(normalizedSearch)
      )
    : [];

  return (
    <fieldset className={`searchMultiFilter ${className}`.trim()}>
      <legend>{label}</legend>
      {selectedOptions.length > 0 && (
        <div className="searchMultiFilter__selected">
          {selectedOptions.map(option => {
            const value = String(option.value);
            return (
              <button
                key={value}
                type="button"
                disabled={disabled.has(value)}
                onClick={event => {
                  event.stopPropagation();
                  onToggle(value);
                }}
                aria-label={`Remove ${option.label}`}
              >
                <span>{option.label}</span>
                <b aria-hidden="true">×</b>
              </button>
            );
          })}
        </div>
      )}
      <input
        type="search"
        value={search}
        onChange={event => setSearch(event.target.value)}
        placeholder={placeholder || `Search ${label.toLocaleLowerCase()}…`}
        autoComplete="off"
      />
      {normalizedSearch && (
        <div className="searchMultiFilter__results" role="listbox" aria-label={`${label} search results`}>
          {matchingOptions.map(option => (
            <button
              key={String(option.value)}
              type="button"
              role="option"
              aria-selected="false"
              onClick={event => {
                event.stopPropagation();
                onToggle(String(option.value));
                setSearch('');
              }}
            >
              {option.label}
            </button>
          ))}
          {matchingOptions.length === 0 && <span>{emptyText}</span>}
        </div>
      )}
    </fieldset>
  );
};
